import { trackUsage } from '@creditsights/api-ui-web';
import classNames from 'classnames/bind';
import { ResizeDirection } from 're-resizable';
import React, { ReactElement, useEffect, useMemo, useRef, useState, MouseEvent } from 'react';
import {
  Loader,
  Icon,
  Button,
  ICONS,
  ButtonMode,
  Divider,
  ContentTypeTag,
  rightSectionWidth,
  DataLoadError,
} from '@creditsights/styleguide-web';
import { Transition } from 'semantic-ui-react';
import { Rnd } from 'react-rnd';
import { ROUTES } from '../../Base.constants';
import { useGetContentHeight } from '../../hooks/layout';
import { useFetchForPrint } from '../../hooks/print';
import { useAddRecentlyViewed } from '../../hooks/recentlyViewed';
import ArticleIncludedContent from '../../pages/Article/ArticleIncludedContent/ArticleIncludedContent';
import NotFound from '../../pages/NotFound';
import { PREVIEW_HEADER_DOM_ID } from './ArticlePreview.constants';
import { ArticlePreviewWidthKey, ARTICLE_PREVIEW_ID, PreviewArticle } from './ArticlePreview.logic';
import { useFetchArticle, usePrepareArticleContent } from '../../hooks/articles';
import ArticleContent from '../../pages/Article/ArticleContent';
import LegalCompliance from '../widgets/LegalCompliance';
import ArticleActions from '../../pages/Article/ArticleActions';
import ArticlePrintView from '../../pages/Article/ArticlePrintView';
import {
  calculateReadTime,
  formatDateTime,
  formatReadTime,
  getArticleComponents,
  isReadTimeVisible,
  isSectorReview,
  overrideArticleComponents,
} from '../../pages/Article/Article.logic';

import styles from './ArticlePreview.module.scss';
import { isNotFound, openWindowInNewTab } from '../../utils/navigationUtils';
import { useWindowResizeListener, useLocalStorage } from '../../hooks/common';

const cx = classNames.bind(styles);
const maxAppWidth = parseFloat(styles.max_app_width);
const transitionDuration = parseFloat(styles.transition_duration);

const getArticlePreviewMaxWidth = () => {
  return Math.round(window.innerWidth < maxAppWidth ? window.innerWidth / 2 : maxAppWidth / 2);
};

interface ArticlePreviewProps {
  articleId: number;
  setArticleId: (articleId: number | null) => void;
  show: boolean;
  isCompact?: boolean;
}

const ArticlePreview = ({ articleId, setArticleId, show, isCompact }: ArticlePreviewProps): ReactElement => {
  const {
    data,
    isLoading: isArticleLoading,
    isRefetching: isArticleRefetching,
    isError,
    refetch,
    error,
  } = useFetchArticle(articleId, true, true);
  const {
    data: articleContent,
    isLoading: isArticleContentLoading,
    isRefetching: isArticleContentRefetching,
  } = usePrepareArticleContent(data);
  const isLoading = isArticleLoading || isArticleRefetching || isArticleContentLoading || isArticleContentRefetching;

  const { renderableComponents } = getArticleComponents(
    articleContent?.content.components || [],
    articleContent?.articleType
  );

  const article = useMemo(
    () => (articleContent ? overrideArticleComponents(articleContent, renderableComponents) : undefined),
    [articleContent]
  );

  const articlePrintRef = useRef<HTMLDivElement>(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [isAnalystWidgetLoading, setIsAnalystWidgetLoading] = useState<boolean>(false);
  const { userFullName, disclaimer, redistributionAllowed, isContentReadyForPrint } = useFetchForPrint(imagesLoaded);
  const addRecentlyViewed = useAddRecentlyViewed();
  const [maxArticlePreviewWidth, setMaxArticlePreviewWidth] = useState<number>(getArticlePreviewMaxWidth);
  const [isArticlePreviewResizing, setIsArticlePreviewResizing] = useState<boolean>(false);
  const [articlePreviewWidth, setArticlePreviewWidth] = useLocalStorage<number>(
    ArticlePreviewWidthKey,
    rightSectionWidth
  );
  const minArticlePreviewWidth = rightSectionWidth;
  const articlePreviewHeaderRef = useRef<HTMLDivElement>(null);
  const articlePreviewContentRef = useRef<HTMLDivElement>(null);
  const articlePreviewHeight =
    (articlePreviewHeaderRef.current?.offsetHeight || 0) + (articlePreviewContentRef.current?.offsetHeight || 0);
  const containerRef = useRef<Rnd | null>(null);

  useEffect(() => {
    // Scrolls preview window to the top when switching between articles
    containerRef.current?.resizableElement?.current?.scrollTo?.({ top: 0 });
  }, [articleId]);

  const onArticleClick = (article: PreviewArticle, event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    openWindowInNewTab(ROUTES.article(article.id));
  };

  const renderHeaderTitle = (title: string): ReactElement => {
    return (
      <div data-testid="preview-article-title" className={cx('title')} dangerouslySetInnerHTML={{ __html: title }} />
    );
  };

  const showReadTime = isReadTimeVisible(article);

  const readTime = useMemo(
    () => article && formatReadTime(calculateReadTime(article.wordCount, article.imageCount)),
    [article]
  );

  const hasPdfFile = useMemo(
    () => article?.content.components.find((component) => !!component.file?.pdfContent),
    [article]
  );
  const formattedPublishDate = useMemo(() => article && formatDateTime(article), [article]);

  useEffect(() => {
    if (article) {
      addRecentlyViewed.mutate(article);

      trackUsage('ARTICLE_VIEW', [
        {
          key: 'articleId',
          value: `${article.id}`,
        },
        {
          key: 'viewMode',
          value: 'preview',
        },
      ]);
    }
  }, [article]);

  const height = useGetContentHeight(isCompact, 24);

  const onResize = () => {
    setMaxArticlePreviewWidth(getArticlePreviewMaxWidth);
  };

  useWindowResizeListener(onResize);

  return (
    <Transition
      transitionOnMount
      animation="slide left"
      duration={transitionDuration}
      unmountOnHide
      mountOnShow
      visible={show}
    >
      <section
        className={cx('body', {
          'is-loading': isLoading,
          'is-error': isError,
          'is-resizing': isArticlePreviewResizing,
          'is-compact': isCompact,
        })}
        id={ARTICLE_PREVIEW_ID}
        data-testid="preview-article"
        style={{
          height: `${height}px`,
          width: `${
            isArticlePreviewResizing ? window.innerWidth : Math.min(articlePreviewWidth, maxArticlePreviewWidth)
          }px`,
        }}
      >
        <Rnd
          disableDragging
          className={cx('resizable-wrapper', { 'is-pdf': hasPdfFile })}
          resizeHandleStyles={{ left: { height: Math.max(articlePreviewHeight, height), zIndex: '2' } }}
          size={{ width: articlePreviewWidth, height: '100%' }}
          minWidth={minArticlePreviewWidth}
          maxWidth={maxArticlePreviewWidth}
          enableResizing={{ left: true }}
          onResizeStart={() => {
            setIsArticlePreviewResizing(true);
          }}
          onResizeStop={(e: Event, dir: ResizeDirection, elementRef: HTMLElement) => {
            setIsArticlePreviewResizing(false);
            setArticlePreviewWidth(elementRef.offsetWidth);
          }}
          ref={containerRef}
        >
          <div className={cx('inner-wrapper')}>
            <header className={cx('header')} id={PREVIEW_HEADER_DOM_ID} ref={articlePreviewHeaderRef}>
              <div className={cx('header-top')}>
                <Button mode={ButtonMode.PLAIN} onClick={() => setArticleId(null)} dataTestId="close-preview">
                  <Icon name={ICONS.Times} />
                </Button>
                {article && renderHeaderTitle(article.content.title)}
              </div>
              {!isLoading && article && (
                <>
                  <Divider />
                  <div className={cx('article-info')}>
                    <ContentTypeTag contentType={article.articleType} />
                    <Divider vertical />
                    <span data-testid="preview-article-publish-date">{formattedPublishDate}</span>
                    {showReadTime && (
                      <>
                        <Divider vertical />
                        <span data-testid="preview-article-read-time">{readTime}</span>
                      </>
                    )}
                  </div>
                  <div className={cx('article-actions')}>
                    <ArticleActions
                      article={article}
                      isArticlePreview
                      articlePrintRef={articlePrintRef}
                      isContentReadyForPrint={isContentReadyForPrint}
                      isAnalystWidgetLoading={isAnalystWidgetLoading}
                    />
                  </div>
                  <Divider />
                </>
              )}
            </header>
            <div className={cx('content')} ref={articlePreviewContentRef}>
              {isLoading && <Loader position="center" />}
              {isError && !isNotFound(error) && <DataLoadError onRetryClick={refetch} />}
              <div className={cx('scroll')}>
                {!isLoading && article && (
                  <>
                    <ArticleContent
                      content={article.content}
                      isArticlePreview
                      summaryClassName={cx('summary')}
                      bodyClassName={cx('content')}
                      setImagesLoaded={setImagesLoaded}
                      onArticleClick={onArticleClick}
                      isSectorReview={isSectorReview(article)}
                    />
                    {!!article.content.includedContent?.length && <Divider />}
                    <ArticleIncludedContent
                      includedContent={article.content.includedContent}
                      onArticleClick={onArticleClick}
                      isSectorReview={isSectorReview(article)}
                      isArticlePreview
                      noSpacing
                      noShadow
                    />
                    <Divider />
                    <LegalCompliance isArticlePreview />
                  </>
                )}
                {isError && isNotFound(error) && <NotFound fullWidth smaller showNavigateToFeeds={false} />}
              </div>
            </div>
          </div>
          {article && (
            <ArticlePrintView
              ref={articlePrintRef}
              article={article}
              userFullName={userFullName}
              disclaimer={disclaimer}
              redistributionAllowed={redistributionAllowed}
              isArticlePreview
              setIsAnalystWidgetLoading={setIsAnalystWidgetLoading}
            />
          )}
        </Rnd>
      </section>
    </Transition>
  );
};

export default ArticlePreview;
